import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject, signal, viewChild } from '@angular/core';
import { I18NextPipe } from 'angular-i18next';
import { ButtonComponent, MbsPopupType, ModalComponent, ModalFooterDirective, ModalService } from 'mbs-ui-kit';
import { PricePipe } from 'mbs-ui-shared@components/licenses/pipes/price.pipe';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicensesService } from 'mbs-ui-shared@services/licenses.service';
import { mediumDateWithTimeMoment } from 'mbs-ui-shared@utils/date';
import { DateTimeFormatterHelper } from 'mbs-ui-shared@utils/helpers/dateTimeFormatterHelper';
import { combineLatest, finalize, map, take } from 'rxjs';
import { SecurityAlertModalComponent } from '../security-alert-modal/security-alert-modal.component';

@Component({
  selector: 'mbs-disable-auto-renewal-modal',
  templateUrl: './disable-auto-renewal-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, CommonModule, I18NextPipe, ModalComponent, ModalFooterDirective, PricePipe]
})
export class DisableAutoRenewalModalComponent implements OnInit {
  private licensesService = inject(LicensesService);
  private modalService = inject(ModalService);
  private auth = inject(AuthFacade);

  readonly baseModal = viewChild.required(ModalComponent);

  readonly alertType = MbsPopupType;
  loading = signal(false);
  loadingReleaseDate = signal(false);
  formattedReleaseDate = signal('');

  ngOnInit(): void {
    if (this.baseModal().data.id && this.baseModal().data.isPremiumSupport) {
      this.setReleaseDate();
    }
  }

  disable(): void {
    this.loading.set(true);

    const request = typeof this.baseModal().data.request === 'function' ? this.baseModal().data.request() : this.baseModal().data.request;

    request
      .pipe(
        take(1),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => this.baseModal().save(),
        error: (error) => {
          const data = error?.error;
          if (data?.typeCode === ErrorCode.DisableTooMuchLicenses) {
            this.modalService
              .openCustom(SecurityAlertModalComponent, { data: { percent: data?.extensions?.percent } })
              .catch(() => this.baseModal().close());
          }
        }
      });
  }

  setReleaseDate(): void {
    this.loadingReleaseDate.set(true);
    combineLatest([this.licensesService.calculateReleaseDate(this.baseModal().data.id), this.auth.currentUser$])
      .pipe(
        take(1),
        map(([{ releaseDate }, user]) =>
          DateTimeFormatterHelper.formatWithOffset(releaseDate, user?.ProviderInfo?.TimeZoneOffset, mediumDateWithTimeMoment)
        )
      )
      .subscribe((formattedDate) => {
        this.formattedReleaseDate.set(formattedDate);
        this.loadingReleaseDate.set(false);
      });
  }
}
